import { type ReactNode, useState } from 'react';
import {
  type CaptionAudit,
  type FramingMode,
  type ShortsParams,
  NEAR_END_SEC,
  type SilenceMode,
  SILENCE_MODES,
  TARGET_DURATION_MAX_SEC,
  TARGET_DURATION_MIN_SEC,
} from '@repo/editor-core';

/**
 * 편집 설정 패널 — «어떻게 만들지»를 대화가 아니라 필드로 받는다.
 *
 * 설계: `docs/shorts-editor-input-redesign.md` D1·D2·D3, 화면은 §10 스트리머 리뷰를
 * 반영한 C안(`.mockups/shorts-editor-params/c.html`).
 *
 * 화면에서 지킨 것:
 *  - **모드 스위치가 없다.** 길이 칩의 «자동»이 곧 자동 모드다. 고를 게 하나 줄어든다.
 *  - 기본 4줄. 화면 위 문구는 «세부 설정»에 접는다 — 리뷰에서 항목이 많으면 이탈했다.
 *  - 무음 값에 **동사**를 넣는다. «빠르게»는 한국어로 배속으로 읽힌다.
 *  - **적용 시점을 숨기지 않는다.** 길이·장면 수는 AI 에디터가 다음 턴에 지키는 «제약»
 *    이고(D1), 말 사이 빈 곳만 지금 바로 잘라낼 수 있는 «동작»이다. 버튼 하나로 뭉뚱그리면
 *    «눌렀는데 길이가 그대로»가 되는데, 그게 이 재설계가 없애려던 문제 그 자체다.
 */

export interface SilencePreviewItem {
  mode: SilenceMode;
  cuts: number;
  removedSec: number;
}

export interface SettingsApi {
  params: ShortsParams;
  /** 폼 값 변경 — 서버에 저장한다(적용은 하지 않는다). */
  onChange: (next: ShortsParams) => void;
  /** 고른 프리셋으로 말 사이 빈 곳을 잘라낸다. */
  onApplySilence: () => void;
  /** 고른 길이로 지금 바로 줄인다. */
  onApplyLength: () => void;
  /** 세로로 자르기 전 **원본 비율** 조각을 받는다(다른 툴에서 이어 작업할 때). */
  onDownloadSource: () => void;
  /** 정점 맛보기를 맨 앞에 붙인다. */
  onColdOpen: () => void;
  /** 웹캠을 구석에 얹는다. 등록이 없으면 안내만 뜬다. */
  onWebcamPip: () => void;
  /** 얹혀 있는 얼굴 PIP 을 뺀다. */
  onRemoveWebcamPip: () => void;
  /** 지금 이 프로젝트에 얼굴 PIP 이 얹혀 있는가. */
  webcamPipApplied: boolean;
  /**
   * 얼굴 PIP 에 **실제로 뭐가 잘려 들어가는지** 보여주는 썸네일.
   *
   * 판정이 아니라 눈으로 보게 하는 것이다 — 실사용자가 자동 감지를 명시적으로 반대했다
   * ("잘못 판정하면 켜져 있는 얼굴을 빼먹는다. 그게 더 나쁘다"). `WebcamPipPreview` 주석 참조.
   */
  webcamPipPreview?: ReactNode;
  /**
   * 자막 자기검사 — **규칙이 결과를 몇 번 고쳤나.**
   *
   * «위반 0건»만 보여주면 «원래 깨끗했다»와 «고쳐서 0이 됐다»가 구분되지 않는다.
   * 실무자 요구 원문: "몇 줄을 어떻게 고쳤다는 카운트만 남겨줘라.
   * **0 이라는 숫자보다 그게 더 쓸모 있다.**"
   */
  captionAudit: CaptionAudit | null;
  /**
   * 가로 화면을 세로로 담는 방식 — **사람이 고른다.**
   *
   * 🔴 16:9 를 9:16 으로 자르면 가로의 **68.4% 가 사라진다.** 그런데 그 자리가 지금까지
   * 언제나 «한가운데»로 못 박혀 있었다. 실물에서 왼쪽 UI·오른쪽 채팅과 **웹캠(얼굴)이
   * 통째로 잘려 나갔다.** 자동 판정(saliency)은 비용 대비 결과가 안 좋았다는 것이
   * 소유자 판단이라, 대신 두 가지 중에 고르게 한다.
   */
  framing: { mode: FramingMode; x: number };
  /**
   * 세로 화면이 실제로 쓰는 가로 폭(0..1). 16:9 면 0.316.
   *
   * 🔴 슬라이더가 이 값을 알아야 한다. 남길 창은 화면 밖으로 못 나가므로 x 는
   * `[kw/2, 1-kw/2]` 로 클램프되는데, 슬라이더를 0~1 로 그리면 **끝으로 밀어도 값이
   * 튕겨 돌아온다**(실측: 0.12 로 밀었더니 0.16 으로 되돌아왔다). 그건 이 저장소가
   * 반복해서 지적받은 «설정했는데 안 먹힌다»와 같은 감각이다.
   * 그래서 슬라이더 양 끝을 **갈 수 있는 양 끝**에 맞춘다.
   */
  framingKeepWidth: number;
  onFramingChange: (next: { mode: FramingMode; x: number }) => void;
  /** 자막을 SRT 로 내려받는다. `source` 면 컷 적용 **전**(원본 조각) 기준. */
  onDownloadSrt: (timebase: 'timeline' | 'source') => void;
  /** 자주 틀리는 이름 등록 창을 연다. */
  onOpenVocabulary: () => void;
  /** 프리셋별 «몇 곳 · 몇 초» 미리보기. 없으면 감지 결과가 없는 클립이다. */
  silencePreview: SilencePreviewItem[] | null;
  /** 웹캠 위치가 등록돼 있는가. */
  webcamConfigured: boolean;
  /**
   * 이 클립에 «어디가 터지는지» 근거(beat sheet)가 있는가.
   *
   * 없으면 길이 자르기·맛보기가 동작하지 않는다. **누르고 나서 «안 된다»고 알려주는 것은
   * 금지다** — 검수 원문: "근거 없는 클립이면 버튼을 띄우지 마라. 누른 다음에 알려주는 건
   * 매번 내 시간을 쓰게 만드는 거다. 그거 없애자고 시작한 거 아니었나."
   */
  hasBeats: boolean;
  busy?: boolean;
  /** 지금 타임라인 길이(초). 사용자가 숫자를 정할 기준이 된다. */
  currentDurationSec: number;
}

const SILENCE_LABEL: Record<SilenceMode, string> = {
  off: '그대로',
  natural: '조금',
  fast: '꽤',
  'extra-fast': '많이',
};

/** 길이 버킷. 실사용에서 나온 값(15·30초)과 쇼츠 상한(60초)을 준다. */
const DURATION_BUCKETS = [15, 30, 60] as const;
/** 장면 수. 4개를 넘으면 15~60초짜리에선 컷이 너무 잘다. */
const SCENE_BUCKETS = [1, 2, 3] as const;

function Chip({
  on,
  onClick,
  disabled,
  children,
  sub,
}: {
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  sub?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full px-3 py-1.5 text-xs leading-tight transition-colors disabled:opacity-40 ${
        on
          ? 'bg-primary font-semibold text-primary-foreground'
          : 'border text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`}
    >
      {children}
      {sub && <span className="mt-px block text-[0.62rem] tabular-nums opacity-65">{sub}</span>}
    </button>
  );
}

/**
 * 켜고 끄는 스위치.
 *
 * `label` 은 선택이 아니다 — 이름 없는 `role="switch"` 는 스크린리더에 «스위치»
 * 한 마디로만 읽힌다(무엇을 켜는 스위치인지 알 수 없다). 왼쪽 라벨은 눈으로만
 * 이어져 있고 접근성 트리에는 연결돼 있지 않다.
 */
function Toggle({
  label,
  on,
  onClick,
  disabled,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={on}
      onClick={onClick}
      disabled={disabled}
      className={`relative h-5 w-9 flex-none rounded-full transition-colors disabled:opacity-40 ${
        on ? 'bg-primary' : 'border bg-secondary'
      }`}
    >
      <span
        className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-all ${
          on ? 'left-4 bg-primary-foreground' : 'left-0.5 bg-muted-foreground'
        }`}
      />
    </button>
  );
}

/**
 * 자막을 몇 번 고쳤는지 한 줄로.
 *
 * 🔴 «위반 0건»만으로는 부족하다. 실무자 원문 —
 * "검사기가 «고치기»까지 하면서 **흔적을 안 남긴다.** 상한 초과 0줄이 «원래 없어서 0»인지
 *  «4.7초를 1.5초로 잘라서 0»인지 결과만 봐서는 구분이 안 된다."
 *
 * 읽기속도 미달은 **비율이 아니라 위치**를 보여준다 — 같은 원문:
 * "38%라는 비율은 의미 없다. 문제는 **위치**다. 클립 마지막 2~3초, 펀치라인에 걸린 자막이
 *  0.6초로 지나가면 그건 조회수를 깎는다. … 비율 말고 «미달 줄이 클립 끝 3초 안에 있는가»만
 *  따로 보여주면 나는 그것만 본다."
 * 그래서 총 미달 수는 괄호로 밀고, **끝 3초** 를 앞에 세운다.
 */
const CaptionAuditLine = ({ audit }: { audit: CaptionAudit | null }) => {
  if (!audit || audit.total === 0) return null;
  const fixes = [
    audit.clamped > 0 && `${audit.clamped}줄 줄임`,
    audit.extended > 0 && `${audit.extended}줄 늘림`,
    audit.merged > 0 && `${audit.merged}번 합침`,
    audit.wrapped > 0 && `${audit.wrapped}줄 줄바꿈`,
  ].filter((x): x is string => typeof x === 'string');

  return (
    <span className="basis-full text-[0.7rem] leading-relaxed text-muted-foreground">
      자막 {audit.total}줄
      {fixes.length > 0 ? ` — 규칙에 맞추느라 ${fixes.join(' · ')}` : ' — 손댈 곳 없었어요'}
      {audit.shortReadingNearEnd > 0 && (
        <>
          {' · '}
          <b className="text-foreground">
            끝 {NEAR_END_SEC}초 안에 빨리 지나가는 자막 {audit.shortReadingNearEnd}줄
          </b>
        </>
      )}
      {audit.shortReading > 0 && ` (전체 빨리 지나감 ${audit.shortReading}줄)`}
    </span>
  );
};

/**
 * 「얼굴」 줄. 등록 상태·적용 상태에 따라 세 얼굴을 한다.
 *
 * 🔴 **얹혀 있을 때는 «세부 설정» 안에 두지 않는다.** 브라우저로 확인하고 옮겼다 —
 * 접힌 칸 안에 있으면 «렌더 전에 보여준다»가 성립하지 않는다. 이 저장소가 이미 같은 판정을
 * 내린 적이 있다(맛보기): "**기본 꺼짐 + 세부 설정 안에 접힘 = 아무도 안 쓴다.**"
 * 지금 영상에 실제로 얹혀 있는 것은 «설정»이 아니라 **결과**다. 결과는 접지 않는다.
 */
const FaceRow = ({ api }: { api: SettingsApi }) => {
  const busy = api.busy;
  return (
    <Row label="얼굴">
      {api.webcamConfigured ? (
        <>
          {api.webcamPipApplied && api.webcamPipPreview}
          <button
            type="button"
            onClick={api.webcamPipApplied ? api.onRemoveWebcamPip : api.onWebcamPip}
            disabled={busy}
            className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-secondary disabled:opacity-40"
          >
            {api.webcamPipApplied ? '얼굴 빼기' : '얼굴 넣기'}
          </button>
          <span className="text-[0.7rem] leading-relaxed text-muted-foreground">
            {api.webcamPipApplied
              ? '얼굴이 아니면 빼세요 — 웹캠을 껐거나 배치를 바꾼 방송이면 엉뚱한 화면이 얹힙니다'
              : '세로로 자를 때 잘린 웹캠을 되살려요'}
          </span>
        </>
      ) : (
        <span className="text-[0.7rem] leading-relaxed text-muted-foreground">
          세로로 자르면 구석의 얼굴이 잘려요.{' '}
          <b className="text-foreground">쇼츠 공통설정 → 웹캠 위치</b>에서 한 번 등록하면 다음
          영상부터 자동으로 얹혀요.
        </span>
      )}
    </Row>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center gap-3 border-b border-border/50 py-3 last:border-b-0">
    <span className="w-[5.5rem] flex-none text-xs">{label}</span>
    {children}
  </div>
);

export function SettingsPanel(api: SettingsApi) {
  const [showMore, setShowMore] = useState(false);
  const { params, busy, currentDurationSec } = api;
  const manual = params.mode === 'manual';
  const targetSec = manual ? params.targetDurationSec : null;
  const sceneCount = manual ? params.sceneCount : null;
  const [customOpen, setCustomOpen] = useState(
    manual && targetSec !== null && !DURATION_BUCKETS.includes(targetSec as never)
  );

  /**
   * 길이·장면 수를 바꾸면 자동으로 수동 모드가 된다.
   *
   * 모드를 따로 고르게 하지 않는 이유(리뷰 §10.1): «맡기기»라는 첫 줄에서 사람들이
   * 막혔다. 값을 정하는 행위 자체가 «직접 정하겠다»는 뜻이므로 그걸 모드로 읽는다.
   */
  const setManual = (patch: { targetDurationSec?: number | null; sceneCount?: number | null }) => {
    api.onChange({
      mode: 'manual',
      targetDurationSec:
        patch.targetDurationSec !== undefined ? patch.targetDurationSec : targetSec,
      sceneCount: patch.sceneCount !== undefined ? patch.sceneCount : sceneCount,
      captions: params.captions,
      headline: params.headline,
      removeSilence: params.removeSilence,
    });
  };

  /** 길이·장면 수를 둘 다 «자동»으로 되돌리면 자동 모드로 돌아간다. */
  const toAuto = () => {
    api.onChange({
      mode: 'auto',
      captions: params.captions,
      headline: params.headline,
      removeSilence: params.removeSilence,
    });
  };

  const setFlag = (patch: Partial<Pick<ShortsParams, 'captions' | 'headline' | 'removeSilence'>>) =>
    api.onChange({ ...params, ...patch } as ShortsParams);

  const previewOf = (mode: SilenceMode) => api.silencePreview?.find((p) => p.mode === mode);
  const silenceAvailable = (api.silencePreview?.length ?? 0) > 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-card">
      {/*
        🔴 `pb-4` — 없으면 **마지막 줄이 영구히 잘린다.** 브라우저로 실제 확인한 결함이다:
        얼굴 썸네일 줄이 마지막일 때 스크롤이 끝까지 가도 캔버스 아래 라벨(«이게 얹혀요»)이
        스크롤 영역 밖에 남았다(실측: 캔버스 bottom 543px == 스크롤러 bottom 543px).
      */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <Row label="길이">
          <div className="flex flex-wrap gap-1.5">
            <Chip
              on={!manual || targetSec === null}
              onClick={() =>
                sceneCount === null ? toAuto() : setManual({ targetDurationSec: null })
              }
              disabled={busy}
            >
              자동
            </Chip>
            {DURATION_BUCKETS.map((sec) => (
              <Chip
                key={sec}
                on={targetSec === sec}
                onClick={() => {
                  setCustomOpen(false);
                  setManual({ targetDurationSec: sec });
                }}
                disabled={busy}
              >
                {sec}초
              </Chip>
            ))}
            <Chip on={customOpen} onClick={() => setCustomOpen(true)} disabled={busy}>
              직접
            </Chip>
          </div>
          {manual && targetSec !== null && api.hasBeats && currentDurationSec > targetSec + 1.2 && (
            // 🔴 «고르는 UI 가 있으면 눌렀을 때 뭔가 되는 게 상식»(검수). 목표보다 길 때만
            // 뜬다 — 이미 짧으면 누를 게 없다.
            <button
              type="button"
              onClick={api.onApplyLength}
              disabled={busy}
              className="rounded-md border border-[hsl(var(--ai)/0.5)] px-2.5 py-1.5 text-xs text-[hsl(var(--ai))] hover:bg-secondary disabled:opacity-40"
            >
              이 길이로 자르기
            </button>
          )}
          {customOpen && (
            <input
              type="number"
              step="0.1"
              min={TARGET_DURATION_MIN_SEC}
              max={TARGET_DURATION_MAX_SEC}
              value={targetSec ?? ''}
              placeholder={currentDurationSec.toFixed(0)}
              disabled={busy}
              onChange={(e) => {
                const v = Number(e.target.value);
                setManual({ targetDurationSec: Number.isFinite(v) && v > 0 ? v : null });
              }}
              className="w-16 rounded-md border border-[hsl(var(--ai)/0.5)] bg-transparent px-2 py-1 text-right text-xs tabular-nums"
              aria-label="길이(초)"
            />
          )}
          {/*
            기준 숫자가 없으면 사용자가 뭘 넣을지 정할 수 없다(리뷰 §10.3-A).
            🔴 예전엔 이걸 입력칸과 **둘 중 하나만** 띄웠다 — 「직접」을 누르는 순간
            기준이 사라지고 빈 칸만 남아서, 정작 숫자가 필요한 순간에 없었다.
            그래서 항상 보여준다.
          */}
          <span className="text-[0.7rem] tabular-nums text-muted-foreground">
            지금 {currentDurationSec.toFixed(1)}초
          </span>
        </Row>

        <Row label="장면 수">
          <div className="flex flex-wrap gap-1.5">
            <Chip
              on={!manual || sceneCount === null}
              onClick={() => (targetSec === null ? toAuto() : setManual({ sceneCount: null }))}
              disabled={busy}
            >
              자동
            </Chip>
            {SCENE_BUCKETS.map((n) => (
              <Chip
                key={n}
                on={sceneCount === n}
                onClick={() => setManual({ sceneCount: n })}
                disabled={busy}
              >
                {n}개
              </Chip>
            ))}
          </div>
          <span className="text-[0.7rem] text-muted-foreground">화면이 몇 번 바뀌는지예요</span>
        </Row>

        {/*
          🔴 «결과를 가장 크게 바꾸는 값»이라 본문 맨 위쪽에 둔다.
          세로로 자르면 가로의 68%가 사라지는데, 그 자리를 못 고르면 무엇이 남을지를
          사람이 통제할 수 없다. 접힌 칸에 넣지 않는다.
        */}
        <Row label="화면">
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip
              on={api.framing.mode === 'fill'}
              onClick={() => api.onFramingChange({ mode: 'fill', x: api.framing.x })}
              disabled={busy}
            >
              꽉 채우기
            </Chip>
            <Chip
              on={api.framing.mode === 'fit'}
              onClick={() => api.onFramingChange({ mode: 'fit', x: api.framing.x })}
              disabled={busy}
            >
              전체 보이기
            </Chip>
          </div>
          {api.framing.mode === 'fill' ? (
            <>
              {(() => {
                // 슬라이더 0~100 을 **갈 수 있는 범위**에 선형으로 얹는다(위 주석 참조).
                const kw = api.framingKeepWidth;
                const lo = kw / 2;
                const span = Math.max(0.0001, 1 - kw);
                const pos = Math.round(((api.framing.x - lo) / span) * 100);
                return (
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={Math.min(100, Math.max(0, pos))}
                    disabled={busy}
                    aria-label="남길 자리"
                    onChange={(e) =>
                      api.onFramingChange({
                        mode: 'fill',
                        x: lo + (Number(e.target.value) / 100) * span,
                      })
                    }
                    className="h-1 w-40 cursor-pointer accent-[hsl(var(--primary))]"
                  />
                );
              })()}
              <span className="text-[0.7rem] text-muted-foreground">
                좌우가 잘려요 — 손잡이를 옮겨 <b className="text-foreground">남길 자리</b>를
                고르세요
              </span>
            </>
          ) : (
            <span className="text-[0.7rem] text-muted-foreground">
              위아래 검은 띠가 생기지만 <b className="text-foreground">잘리는 게 없어요</b>
            </span>
          )}
        </Row>

        <Row label="자막">
          <Toggle
            label="자막"
            on={params.captions}
            onClick={() => setFlag({ captions: !params.captions })}
            disabled={busy}
          />
          <span className="text-[0.7rem] text-muted-foreground">
            말한 내용에서 자동으로 만들어요
          </span>
          <div className="flex-1" />
          <button
            type="button"
            onClick={api.onOpenVocabulary}
            className="rounded-md border border-[hsl(var(--ai)/0.45)] px-2.5 py-1.5 text-xs hover:bg-secondary"
          >
            자주 틀리는 이름
          </button>
          {params.captions && <CaptionAuditLine audit={api.captionAudit} />}
        </Row>

        {/*
          🔴 **얹혀 있으면 본문에 둔다.** 지금 영상에 실제로 붙어 있는 것은 «설정»이 아니라
          결과이고, 결과를 접힌 칸 안에 숨기면 «렌더 전에 보여준다»가 성립하지 않는다.
          (안 얹혀 있을 때는 «세부 설정» 안에 남는다 — 그때는 정말 설정이다)
        */}
        {api.webcamPipApplied && <FaceRow api={api} />}

        <Row label="말 사이 빈 곳">
          <div className="flex flex-wrap gap-1.5">
            {SILENCE_MODES.map((mode) => {
              const p = previewOf(mode);
              return (
                <Chip
                  key={mode}
                  on={params.removeSilence === mode}
                  onClick={() => setFlag({ removeSilence: mode })}
                  disabled={busy || (mode !== 'off' && !silenceAvailable)}
                  sub={p && p.cuts > 0 ? `${p.cuts}곳 ${p.removedSec.toFixed(1)}초` : undefined}
                >
                  {SILENCE_LABEL[mode]}
                </Chip>
              );
            })}
          </div>
          {!silenceAvailable && (
            // 막다른 길을 만들지 않는다 — 왜 안 되는지와 무엇을 하면 되는지를 같이 말한다.
            <span className="text-[0.7rem] text-muted-foreground">
              이 클립은 빈 곳 정보가 없어요. 다시 분석하면 쓸 수 있어요.
            </span>
          )}
        </Row>

        {/*
          🔴 «세부 설정» 안에 접어 두지 않는다.
          검수 원문: "**기본 꺼짐 + 세부 설정 안에 접힘 = 아무도 안 쓴다.** 길이를 1.5초로
          늘린 건 아무도 안 켜는 기능을 더 잘 만든 것이다. … 선택지는 둘뿐이다. 기본 켬으로
          바꾸거나 지운다. **지금처럼 두는 건 제일 나쁘다.**"
          기본 켬으로 못 하는 이유는 하나다 — 같은 대사가 두 번 나오는 것이 촌스러운지
          **실물로 확인한 적이 없다**(태스크 #19). 그 전까지는 최소한 **보이게** 둔다.
        */}
        {api.hasBeats && (
          <Row label="맛보기">
            <button
              type="button"
              onClick={api.onColdOpen}
              disabled={busy}
              className="rounded-md border px-3 py-1.5 text-xs hover:bg-secondary disabled:opacity-40"
            >
              앞에 붙이기
            </button>
            <span className="text-[0.7rem] text-muted-foreground">
              제일 터지는 순간 1.5초를 맨 앞에 넣어 «뭔 상황인지»를 먼저 보여줘요
            </span>
          </Row>
        )}

        <button
          type="button"
          onClick={() => setShowMore((v) => !v)}
          className="flex w-full items-center gap-1.5 border-t border-border/50 py-2.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <span className={`text-[0.55rem] transition-transform ${showMore ? 'rotate-90' : ''}`}>
            ▶
          </span>
          세부 설정
        </button>

        {showMore && (
          <div className="pb-1">
            <Row label="화면 위 문구">
              <Toggle
                label="화면 위 문구"
                on={params.headline}
                onClick={() => setFlag({ headline: !params.headline })}
                disabled={busy}
              />
              <span className="text-[0.7rem] text-muted-foreground">
                영상에서 한 말로 짧게 얹어요 (유튜브 제목과 달라요)
              </span>
            </Row>
            {/*
              🔴 등록 전에는 **버튼을 아예 안 보여준다.**
              "없는 방으로 사람을 보냈다. 차라리 버튼이 없는 게 낫다. 없으면 «아직 안
              되는구나» 하고 넘어가는데, 있으면 **내가 뭘 잘못한 줄 알고 5분 헤맨다.**"
              (등록해 두면 프로젝트를 열 때 자동으로 얹히므로 이 버튼은 «다시 얹기»용이다)
            */}
            {/*
              🔴 **뺄 수단이 있어야 한다.**
              얼굴 PIP 은 프로젝트를 열 때 자동으로 얹힌다. 그런데 웹캠을 껐거나 화면
              배치를 바꾼 방송에서는 등록해 둔 좌표가 틀린 좌표가 되고, 그 자리의 미니맵이
              얹힌다. 실물 검수에서 정확히 그게 나왔다 —
              "웹캠 안 켠 방송인데 미니맵 잘린 조각이 네모나게 얹혀 있으면 그건 편집 실수처럼
               보인다. 댓글에 «저거 뭐임» 부터 달린다. **저것만 빼면 올린다.**"
            */}
            {/*
              🔴 **얹히는 화면을 렌더 전에 보여준다.**
              등록해 둔 좌표가 이 방송에서도 얼굴인지는 기계가 알 수 없다. 그래서 판정하지 않고
              «이게 얹혀요» 를 그대로 보여주고, 그 옆에 「얼굴 빼기」를 같이 둔다. 실사용자 원문 —
              "렌더 다 끝난 뒤에야 알게 되는 구조면 그건 사람한테 떠넘긴 게 맞다.
               렌더 전에 보이면 그냥 확인 절차다. **그 차이는 크다.**"
            */}
            {!api.webcamPipApplied && <FaceRow api={api} />}
            {/*
              다른 툴로 가져가는 사람을 위한 두 벌. 실무자 검수 원문 —
              "내가 원하는 산출물은 mp4 가 아니라 «타임코드 + SRT»다. … 그 조각이
              **세로로 크롭되기 전 원본 비율**이어야 한다. 이미 가운데 크롭된 세로 mp4 만
              준다면 나는 얼굴 잘린 소스를 받는 거고, 그럼 아무것도 못 쓴다."
              하이라이트 클립은 `-c copy` 로 잘려 원본 16:9 그대로다 — 링크만 주면 된다.
            */}
            <Row label="다른 툴로">
              <button
                type="button"
                onClick={api.onDownloadSource}
                className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-secondary"
              >
                원본 조각
              </button>
              <button
                type="button"
                onClick={() => api.onDownloadSrt('source')}
                className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-secondary"
              >
                SRT (원본 기준)
              </button>
              <button
                type="button"
                onClick={() => api.onDownloadSrt('timeline')}
                className="rounded-md border px-2.5 py-1.5 text-xs hover:bg-secondary"
              >
                SRT (편집본 기준)
              </button>
            </Row>
            <Row label="">
              <span className="text-[0.7rem] leading-relaxed text-muted-foreground">
                「원본 조각」은 세로로 자르기 전 가로 원본이에요. 캡컷·프리미어로 가져갈 거면
                <b className="text-foreground"> 원본 조각 + SRT(원본 기준)</b> 을 같이 받으세요 —
                둘의 시간이 서로 맞아요.
              </span>
            </Row>
          </div>
        )}
      </div>

      {/*
        적용 시점이 두 가지라는 것을 **숨기지 않는다**.

        길이·장면 수·자막·문구는 AI 에디터가 다음에 만들 때 지키는 **제약**이고(D1 —
        대화에서 소실될 수 없게 필드로 받는 것이 목적이다), 말 사이 빈 곳만 지금 바로
        잘라낼 수 있는 **동작**이다. 버튼 하나로 뭉뚱그리면 «눌렀는데 길이가 그대로»가
        되고, 그건 이 재설계가 없애려던 «말했는데 안 먹힌다» 그 자체다.
      */}
      <div className="flex flex-none items-center gap-3 border-t px-4 py-3">
        <button
          type="button"
          onClick={api.onApplySilence}
          disabled={busy || params.removeSilence === 'off' || !silenceAvailable}
          className="rounded-lg bg-[hsl(var(--ai))] px-4 py-2 text-[0.82rem] font-bold tracking-tight text-[#160b06] transition-[filter] hover:brightness-110 disabled:opacity-40"
        >
          {busy ? '자르는 중…' : '빈 곳 잘라내기'}
        </button>
        <span className="text-[0.7rem] leading-relaxed text-muted-foreground">
          {!silenceAvailable
            ? '이 클립은 빈 곳 정보가 없어서 못 잘라요.'
            : params.removeSilence === 'off'
              ? '위에서 얼마나 줄일지 먼저 고르세요.'
              : '자동 자막도 같이 새로 깔려요. 직접 넣은 자막·GIF 는 그대로 있고, 마음에 안 들면 ↶ 실행취소 한 번이면 돌아와요.'}
        </span>
      </div>
    </div>
  );
}
